import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { SuscriptionItemAvailability, UsuarioDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    errorMessage,
    initials,
    NEW_USER_PATH,
    personName,
    STATUS_LABELS,
    statusTone,
    userPath,
    USERS_BACK_PATH,
    USERS_PATH,
} from '../common/user-common'
import type { FormMessage, UsersLocationState } from '../common/user-common'

const LOAD_ERROR = 'No se pudo cargar el listado de usuarios.'
const DELETE_ERROR = 'No se pudo eliminar al usuario.'

export interface UserRow {
    id: number
    name: string
    initials: string
    email: string
    phone: string
    status: string
    statusTone: string
}

export interface UsageSummary {
    used: number
    total: number
    available: number
    tone: 'info' | 'warning'
    text: string
}

const toRow = (usuario: UsuarioDTO): UserRow => {
    const name = personName(usuario)
    return {
        id: usuario.id,
        name,
        initials: initials(name),
        email: String(usuario.correo || '').trim(),
        phone: String(usuario.celular || '').trim(),
        status: (usuario.status && STATUS_LABELS[usuario.status]) || 'Sin estado',
        statusTone: statusTone(usuario.status),
    }
}

const toUsage = (usage: SuscriptionItemAvailability | null): UsageSummary | null => {
    if (!usage) return null
    const total = Number(usage.quantity) || 0
    const used = Number(usage.usage) || 0
    const available = total - used
    if (total <= 1) return { used, total, available, tone: 'warning', text: 'Tu suscripción no incluye usuarios adicionales.' }
    if (available <= 0) return { used, total, available, tone: 'warning', text: 'Tu suscripción llegó al límite de usuarios disponibles.' }
    return { used, total, available, tone: 'info', text: `Te ${available === 1 ? 'queda 1 usuario disponible' : `quedan ${available} usuarios disponibles`}.` }
}

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const useUserListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const locationState = location.state as UsersLocationState | null

    const [search, setSearch] = useState('')
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [message, setMessage] = useState<FormMessage | null>(locationState?.flash ? { type: 'success', message: locationState.flash } : null)
    const [items, setItems] = useState<UsuarioDTO[]>([])
    const [usage, setUsage] = useState<SuscriptionItemAvailability | null>(null)
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (locationState?.flash) history.replace(USERS_PATH)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const usersResponse: WrapperResponse<UsuarioDTO[]> | null = await (await APIInvoke.invokeGET('/usuarios/adicionales')).json()
                if (!active) return
                if (!usersResponse?.ok) throw new Error(usersResponse?.message || LOAD_ERROR)
                setItems(usersResponse.body || [])
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
            try {
                const usageResponse: WrapperResponse<SuscriptionItemAvailability> | null = await (await APIInvoke.invokeGET('/suscripciones/usages?tipoItem=USUARIO')).json()
                if (active && usageResponse?.ok) setUsage(usageResponse.body)
            } catch (usageError) {
                console.error(usageError)
            }
        }
        load()
        return () => { active = false }
    }, [reloadToken])

    const rows = useMemo(() => {
        const query = normalize(search.trim())
        const all = items.map(toRow).sort((left, right) => left.name.localeCompare(right.name, 'es'))
        if (!query) return all
        return all.filter((row) => normalize(`${row.name} ${row.email}`).includes(query))
    }, [items, search])

    const confirmDelete = async () => {
        if (deleteId == null || deleting) return
        try {
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/usuarios/${deleteId}`)).json()
            if (response.ok) {
                setItems((current) => current.filter((item) => item.id !== deleteId))
                setMessage({ type: 'success', message: response.message || 'Usuario eliminado correctamente' })
                setReloadToken((token) => token + 1)
            } else {
                setMessage({ type: 'error', message: response.message || DELETE_ERROR })
            }
        } catch (deleteError) {
            console.error(deleteError)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
            setDeleteId(null)
        }
    }

    const userToDelete = items.find((item) => item.id === deleteId)

    return {
        search,
        loading,
        initialLoadDone,
        error,
        message,
        rows,
        totalItems: items.length,
        usage: toUsage(usage),
        backTo: USERS_BACK_PATH,
        newLocation: { pathname: NEW_USER_PATH, state: { backUrl: USERS_PATH } },
        deleteOpen: deleteId != null,
        deleteName: userToDelete ? personName(userToDelete) || userToDelete.correo || 'este usuario' : 'este usuario',
        deleting,
        changeSearch: setSearch,
        clearSearch: () => setSearch(''),
        reload: () => setReloadToken((token) => token + 1),
        editUser: (id: number) => history.push({ pathname: userPath(id), state: { backUrl: USERS_PATH } }),
        askDelete: (id: number) => setDeleteId(id),
        cancelDelete: () => setDeleteId(null),
        confirmDelete,
        dismissMessage: () => setMessage(null),
    }
}
